import React from 'react';
import SurveyForm from './SurveyForm';
import AnalysisProgress from './AnalysisProgress';
import ResultsDashboard from './ResultsDashboard';
import SelfHostNotice from './SelfHostNotice';
import { SurveyAnalysisRequest, SurveyAnalysisResult, AnalysisStatus } from '../types/survey';

type AppState = 'form' | 'analyzing' | 'results';

interface HomeTabProps {
  liveTakeEnabled: boolean;
  liveTakeReason?: 'missing_extra' | 'missing_api_key' | null;
  appState: AppState;
  analysisId: string;
  analysisResult: SurveyAnalysisResult | null;
  isLoading: boolean;
  error: string;
  onSubmitAnalysis: (request: SurveyAnalysisRequest) => void;
  onStatusUpdate: (status: AnalysisStatus) => void;
  onAnalysisComplete: () => void;
  onExportReport: () => void;
  onStartNew: () => void;
}

const HomeTab: React.FC<HomeTabProps> = ({
  liveTakeEnabled,
  liveTakeReason,
  appState,
  analysisId,
  analysisResult,
  isLoading,
  error,
  onSubmitAnalysis,
  onStatusUpdate,
  onAnalysisComplete,
  onExportReport,
  onStartNew,
}) => {
  if (!liveTakeEnabled) {
    return <SelfHostNotice reason={liveTakeReason} />;
  }

  return (
    <>
      {/* Hero Section - only show on form state */}
      {appState === 'form' && (
        <div className="text-center mb-12">
          <h2 className="text-4xl font-bold text-gray-900 mb-4">
            Analyze online surveys to identify bot-detection mechanisms.
          </h2>
        </div>
      )}

      {/* Error Display */}
      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
          <p className="text-red-800">{error}</p>
          {appState !== 'form' && (
            <button
              onClick={onStartNew}
              className="mt-2 text-sm text-red-600 hover:text-red-700 underline"
            >
              Start new analysis
            </button>
          )}
        </div>
      )}

      {/* Content based on state */}
      <div className="max-w-4xl mx-auto">
        {appState === 'form' && (
          <SurveyForm onSubmit={onSubmitAnalysis} loading={isLoading} />
        )}

        {appState === 'analyzing' && (
          <div className="space-y-6">
            <AnalysisProgress
              analysisId={analysisId}
              onStatusUpdate={onStatusUpdate}
              onComplete={onAnalysisComplete}
            />
            <div className="text-center">
              <button
                onClick={onStartNew}
                className="text-sm text-gray-500 hover:text-gray-700 underline"
              >
                Start new analysis
              </button>
            </div>
          </div>
        )}

        {appState === 'results' && analysisResult && (
          <div className="space-y-6">
            <ResultsDashboard
              result={analysisResult}
              onExport={onExportReport}
            />
            <div className="text-center">
              <button
                onClick={onStartNew}
                className="px-6 py-3 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
              >
                Analyze Another Survey
              </button>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default HomeTab;
